import { useSelector } from '@xstate/react'
import Head from 'next/head'
import { useRouter } from 'next/router'
import React from 'react'
import styled from 'styled-components'
import { assertExists } from 'utils/assert'
import { resizedImageUrl } from 'utils/imageUrl'
import { media } from 'utils/media'
import { RatingDistribution } from 'lib/RatingDistribution'
import { StarRating } from 'lib/StarRating'
import { Text } from 'lib/Text'
import { useBrandReviewSummary } from 'modules/brands/queries/useBrandReviewSummary'
import { useCurrentBrand } from 'modules/brands/show/context/CurrentBrandProvider'
import { WriteOrEditReviewButton } from 'modules/member/lists/components/WriteOrEditReviewButton'
import { useBrandReviewsContext } from '../context/BrandReviewsContext'

const AverageRating = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`

const AverageRatingValue = styled(Text)`
  margin-right: 16px;
`

const WouldRecommendValue = styled(Text)`
  display: flex;
  align-items: flex-start;
`

const Percent = styled(Text)`
  line-height: 34px;
`

const StyledWriteOrEditReviewButton = styled(WriteOrEditReviewButton)`
  flex: 1;
  ${media.tabletDown} {
    display: none;
  }
`

const SummaryContainer = styled.div`
  grid-gap: 24px;
  padding: 32px;
  margin-bottom: 24px;
  border: var(--border);
  border-color: var(--neutral-20);

  align-items: center;
  display: flex;
  flex-wrap: wrap;

  ${media.tabletDown} {
    padding: 24px;
  }
`

const SummaryBlock = styled.div`
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  flex: 1 0 120px;
  ${media.tabletDown} {
    flex: 0 1 200px;
  }

  ${media.phoneDown} {
    flex: 1 0 120px;
  }
`

export const Summary = () => {
  const { push } = useRouter()
  const { brand } = useCurrentBrand()
  const { data, loading } = useBrandReviewSummary({ id: brand.id })
  const { reviewDiscoveryService } = useBrandReviewsContext()
  assertExists(reviewDiscoveryService)
  const rating = useSelector(reviewDiscoveryService, (state) => state.context.rating)

  const summary = data?.brand?.reviewSummary

  if (loading || !summary) {
    return null
  }

  const handleRatingClick = (val: number) => {
    const newRating = String(val) === rating ? null : String(val)

    reviewDiscoveryService.send('FILTER_RATING', { rating: newRating })

    if (!window.location.pathname.includes('/reviews')) {
      push(`/brands/${brand.slug}/reviews`)
    }

    window.analytics.track('Reviews Rating Distribution Clicked', {
      brandId: brand.id,
      ratings: newRating
    })
  }

  const roundedRating = summary.averageRating ? +summary.averageRating.toFixed(1) : 0

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org/',
              '@type': 'AggregateRating',
              '@id': `https://thingtesting.com/brands/${brand.slug}/reviews`,
              'itemReviewed': {
                '@type': 'Organization',
                '@id': `https://thingtesting.com/brands/${brand.slug}`,
                'image': resizedImageUrl(brand.primaryImageFullPath, { width: 1080, height: 1080 }),
                'name': brand.title,
                'description': brand.description
              },
              'ratingValue': summary.averageRating,
              'ratingCount': summary.ratingsCount,
              'bestRating': 5,
              'worstRating': 1
            })
          }}
        />
      </Head>

      <SummaryContainer>
        {summary.averageRating && (
          <SummaryBlock>
            <AverageRating>
              <AverageRatingValue size="xl" weight="bold">
                {roundedRating}
              </AverageRatingValue>
              <StarRating stars={roundedRating} />
            </AverageRating>
            <Text weight="medium" size="small" color="neutral-60">
              Average rating from {summary.ratingsCount} {summary.ratingsCount === 1 ? 'user' : 'users'}
            </Text>
          </SummaryBlock>
        )}

        {summary.wouldRecommendPercent != null && summary.ratingsCount > 3 && (
          <SummaryBlock>
            <WouldRecommendValue size="xl" weight="bold">
              {summary.wouldRecommendPercent}
              <Percent as="span" size="p" weight="bold">
                %
              </Percent>
            </WouldRecommendValue>
            <Text weight="medium" size="small" color="neutral-60">
              Recommend to a friend
            </Text>
          </SummaryBlock>
        )}

        <RatingDistribution
          selectedRating={rating ? +rating : undefined}
          total={summary.ratingsCount}
          ratingDistribution={summary.ratingDistribution}
          onRatingClick={handleRatingClick}
        />

        {!brand.rip && (
          <StyledWriteOrEditReviewButton brandId={brand.id} brandTitle={brand.title} brandSlug={brand.slug} reviewId={brand.myReview?.id} />
        )}
      </SummaryContainer>
    </>
  )
}
